import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Stack
} from '@mui/material';
import Grid from '@mui/material/Grid';

import QuizIcon from '@mui/icons-material/Quiz';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

export default function SkillsAssessment() {
  const assessments = [
    {
      id: 1,
      title: 'Tax Preparation Fundamentals',
      category: 'Taxation',
      questions: 25,
      duration: 30,
      completed: true,
      score: 88,
    },
    {
      id: 2,
      title: 'Financial Accounting Basics',
      category: 'Accounting',
      questions: 30,
      duration: 45,
      completed: false,
      score: null,
    },
    {
      id: 3,
      title: 'Auditing Standards',
      category: 'Auditing',
      questions: 20,
      duration: 25,
      completed: false,
      score: null,
    },
  ];

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
          Skills Assessment
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Test your knowledge and identify areas for improvement
        </Typography>
      </Box>

      {/* Assessment Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {assessments.map((assessment) => (
          <Grid size={{ xs: 12, md: 6 }} key={assessment.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Header with Category and Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Chip
                    label={assessment.category}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                  {assessment.completed && (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Completed"
                      color="success"
                      size="small"
                    />
                  )}
                </Box>

                {/* Title */}
                <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                  {assessment.title}
                </Typography>

                {/* Assessment Details */}
                <Stack spacing={1} sx={{ mt: 2, mb: 3, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <QuizIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {assessment.questions} questions
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {assessment.duration} minutes
                    </Typography>
                  </Box>
                  {assessment.completed && assessment.score !== null && (
                    <Typography variant="h6" color="primary.main" fontWeight="bold" sx={{ mt: 1 }}>
                      Score: {assessment.score}%
                    </Typography>
                  )}
                </Stack>

                {/* Action Button */}
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                >
                  {assessment.completed ? 'Retake Assessment' : 'Start Assessment'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Assessment Info Alert */}
      <Alert
        severity="info"
        icon={<LightbulbIcon />}
        sx={{
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          About Skills Assessments
        </Typography>
        <Typography variant="body2">
          These assessments help you identify your strengths and areas for improvement. 
          Based on your results, we'll recommend relevant courses to help you enhance your skills. 
          You can retake assessments at any time to track your progress.
        </Typography>
      </Alert>
    </Box>
  );
}
